import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUp, Trash2, ShieldCheck } from "lucide-react";
import { api, API, refreshSession, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RichText, PlusGate } from "@/components/Common";
import { OfferChips } from "@/v2/Plans";
import { RateBar, StyleOffer } from "@/v2/ChatExtras";
import { CLEAR_CHAT_CONFIRM } from "@/lib/privacyCopy";

const PROMPTS = ["Why am I so tired lately?", "Help me wind down tonight", "What should I focus on this week?", "How does my cycle affect my energy?"];

const SEND_FAILED = "That didn't send. Your connection may have dropped, but your message is kept here. Tap Retry when you're ready.";

async function streamChat(text, onDelta, endpoint = "/chat/stream", extra = {}, onOffers = () => {}, onIds = () => {}) {
  const post = () => fetch(`${API}${endpoint}`, {
    method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text, ...extra }),
  });
  let res = await post();
  if (res.status === 401) {
    await refreshSession().catch(() => null);
    res = await post();
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.detail || "Couldn't reach Companion");
    err.status = res.status;
    throw err;
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  let failed = false;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      const line = p.replace(/^data: /, "");
      try { const ev = JSON.parse(line); if (ev.error) failed = true; if (ev.delta) onDelta(ev.delta); if (ev.offers) onOffers(ev.offers, ev.msg_id); if (ev.ids) onIds(ev.ids); if (ev.style_offer) onIds({ style_offer: ev.style_offer }); } catch (e) { console.warn("Skipped malformed chat event", e.message); }
    }
  }
  return { failed };
}

const msg = (role, content) => ({ id: crypto.randomUUID(), role, content });

const Typing = () => (
  <span className="inline-flex gap-1"><span className="h-2 w-2 rounded-full bg-stone-300 animate-bounce" /><span className="h-2 w-2 rounded-full bg-stone-300 animate-bounce [animation-delay:.15s]" /><span className="h-2 w-2 rounded-full bg-stone-300 animate-bounce [animation-delay:.3s]" /></span>
);

function Bubble({ m, i, onRetry, onOffers, onForget, onPatch }) {
  const mine = m.role === "user";
  return (
    <div className={`flex flex-col ${mine ? "items-end" : "items-start"}`} data-testid={`companion-msg-${m.role}-${i}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-[15px] leading-relaxed ${mine ? "bg-stone-900 text-stone-50 rounded-br-md" : "card rounded-bl-md text-stone-800"}`}>
        {mine ? m.content : m.content ? <RichText text={m.content} /> : m.failed ? <span data-testid="companion-failed-text">I couldn't finish that reply. Tap Retry and I'll try again.</span> : <Typing />}
        {!mine && m.offers?.length > 0 && <OfferChips offers={m.offers} msgId={m.id} onChange={onOffers} />}
        {m.failed && onRetry && <button onClick={onRetry} data-testid="companion-retry-btn" className="mt-2 px-4 h-10 rounded-full border border-stone-300 text-sm font-semibold">Retry</button>}
      </div>
      {mine && m.unsent && <span data-testid={`companion-unsent-${i}`} className="mt-1 text-[11px] text-[#A4532F]">Not sent yet</span>}
      {!mine && onPatch && <StyleOffer offer={m.style_offer} onDone={() => onPatch({ style_offer: null })} />}
      {!mine && onPatch && <RateBar m={m} i={i} onChange={(rating) => onPatch({ rating })} onSuggest={(style_offer) => onPatch({ style_offer })} />}
      {mine && onForget && <button onClick={onForget} data-testid={`companion-forget-${i}`} className="min-h-11 inline-flex items-center px-1 text-[11px] text-stone-400 hover:text-stone-700 underline-offset-2 hover:underline">Forget this</button>}
    </div>
  );
}

function Header({ heading, subline, v2, priv, setPriv, canClear, onClear }) {
  return (
    <div className="flex items-start justify-between mb-5 rise">
      <div>
        <div className="eyebrow mb-2">Companion</div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight">{heading}</h1>
        <p className="text-xs text-stone-500 mt-2 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-[#6E8B74]" />{subline}</p>
        {v2 && (
          <label className="mt-3 inline-flex items-center gap-2 text-xs text-stone-600 cursor-pointer" data-testid="companion-private-toggle">
            <input type="checkbox" checked={priv} onChange={(e) => setPriv(e.target.checked)} className="accent-[#5A3E4E]" />
            Private conversation: not saved, nothing learned
          </label>
        )}
      </div>
      {canClear && <button onClick={onClear} data-testid="companion-clear-btn" className="text-stone-400 hover:text-red-600 p-3" aria-label="Clear conversation"><Trash2 className="h-4 w-4" /></button>}
    </div>
  );
}

const MAX_CHARS = 6000;

function Composer({ text, setText, send, busy }) {
  const ta = useRef(null);
  useEffect(() => { const el = ta.current; if (el) { el.style.height = "auto"; el.style.height = `${Math.min(el.scrollHeight, 128)}px`; } }, [text]);
  const onPaste = (e) => {
    const pasted = e.clipboardData?.getData("text") || "";
    if (text.length + pasted.length > MAX_CHARS) toast.message(`That's a long one. I can read up to ${MAX_CHARS.toLocaleString()} characters, so the end was cut. Send the rest in a second message.`);
  };
  return (
    <form onSubmit={(e) => { e.preventDefault(); send(); }} className="card p-2 flex items-end gap-2 shadow-lg bg-white/95 backdrop-blur relative">
      {text.length > MAX_CHARS - 1000 && <span data-testid="companion-char-count" className="absolute -top-7 right-3 text-xs text-stone-600 bg-white/95 rounded-full px-2 py-0.5 shadow-sm">{text.length.toLocaleString()} / {MAX_CHARS.toLocaleString()}</span>}
      <textarea ref={ta} data-testid="companion-chat-input" value={text} onChange={(e) => setText(e.target.value)} onPaste={onPaste} rows={1} maxLength={MAX_CHARS}
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
        placeholder="Ask about how you're feeling…" className="flex-1 resize-none bg-transparent px-3 py-2.5 text-[15px] focus:outline-none max-h-32" />
      <button disabled={busy || !text.trim()} onMouseDown={(e) => e.preventDefault()} data-testid="companion-send-btn" className="btn-terra h-11 w-11 shrink-0 rounded-full grid place-items-center disabled:opacity-40" aria-label="Send"><ArrowUp className="h-4 w-4" /></button>
    </form>
  );
}

export default function Companion({ endpoint = "/chat/stream", prompts = PROMPTS, heading = "Talk it through", subline = "Knows your recent check-ins. Not a doctor; it will tell you when to see one.", v2 = false, opener = null }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [usage, setUsage] = useState(null);
  const [priv, setPriv] = useState(false);
  const loc = useLocation();
  const [fromToday, setFromToday] = useState(v2 ? loc.state?.fromToday || null : null);
  const endRef = useRef(null);
  const inFlight = useRef(false);

  const loadUsage = () => api.get("/chat/usage").then(({ data }) => setUsage(data));
  useEffect(() => {
    api.get("/chat/messages").then(({ data }) => setMsgs(opener ? [...data, msg("assistant", opener)] : data));
    loadUsage();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [msgs]);
  useEffect(() => {
    if (fromToday) { setText((x) => x || `Can we talk through "${fromToday.text}"?`); window.history.replaceState({}, ""); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (t, retry = false, sent = null) => {
    const q = (t ?? text).trim();
    if (!q || busy || inFlight.current) return;
    inFlight.current = true;
    if (!retry) setText("");
    setBusy(true);
    const body = sent || (opener && msgs.length <= 1 && !retry ? `(Replying to your question "${opener}") ${q}` : q).slice(0, MAX_CHARS);
    setMsgs((m) => (retry ? [...m.slice(0, -1), msg("assistant", "")] : [...m, { ...msg("user", q), sent: body }, msg("assistant", "")]));
    try {
      const { failed } = await streamChat(body, (d) => setMsgs((m) => {
        const c = [...m];
        c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + d };
        return c;
      }), endpoint, { ...(v2 ? { private: priv } : {}), ...(fromToday ? { context: fromToday } : {}), retry }, (offers, id) => setMsgs((m) => {
        const c = [...m];
        c[c.length - 1] = { ...c[c.length - 1], offers, id: id || c[c.length - 1].id };
        return c;
      }), (ids) => setMsgs((m) => {
        const c = [...m];
        if (ids.user && c[c.length - 2]?.role === "user") c[c.length - 2] = { ...c[c.length - 2], id: ids.user };
        if (ids.reply) c[c.length - 1] = { ...c[c.length - 1], id: c[c.length - 1].id || ids.reply };
        if (ids.style_offer) c[c.length - 1] = { ...c[c.length - 1], style_offer: ids.style_offer };
        return c;
      }));
      setMsgs((m) => {
        const c = [...m], u = c.length - 2;
        if (c[u]?.unsent) c[u] = { ...c[u], unsent: false };
        if (failed) c[c.length - 1] = { ...c[c.length - 1], failed: true };
        return c;
      });
      if (!failed) setFromToday(null);
    } catch (e) {
      const tell = e.status >= 400 && e.status < 500 ? e.message : SEND_FAILED;
      setMsgs((m) => {
        const c = [...m], u = c.length - 2;
        if (c[u]?.role === "user") c[u] = { ...c[u], unsent: true };
        c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content || tell, failed: true, local: true };
        return c;
      });
      toast.error(tell);
    }
    inFlight.current = false;
    setBusy(false);
    loadUsage();
  };

  const forget = async (i) => {
    if (!window.confirm("Forget this message? CAMRYN deletes it, its reply, memories and missed-step reasons learned from it, and resets the chat summary. Plans you chose to save stay.")) return;
    try {
      const { data } = await api.delete(`/v2/chat/messages/${msgs[i].id}`);
      setMsgs((all) => all.filter((_, k) => k !== i && !(k === i + 1 && all[k].role === "assistant")));
      toast.success(data.removed_memories ? `Forgotten, with ${data.removed_memories} ${data.removed_memories === 1 ? "memory" : "memories"} learned from it` : "Forgotten");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const clear = async () => {
    if (!window.confirm(CLEAR_CHAT_CONFIRM)) return;
    try { await api.delete("/chat/messages"); setMsgs([]); toast.success("Chat history deleted"); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!user.ai_consent) return <div className="card p-6" data-testid="companion-ai-off">AI is turned off in your Privacy Center. Turn it on there to talk with Companion.</div>;
  const outOfFree = usage?.limit != null && usage.used >= usage.limit;

  return (
    <div className="flex flex-col min-h-[calc(100vh-11rem)]">
      <Header heading={heading} subline={subline} v2={v2} priv={priv} setPriv={setPriv} canClear={msgs.length > 0} onClear={clear} />

      <div className="flex-1 space-y-4" data-testid="companion-messages">
        {msgs.length === 0 && (
          <div className="grid sm:grid-cols-2 gap-2.5 rise d1">
            {prompts.map((p, i) => (
              <button key={p} onClick={() => send(p)} disabled={outOfFree} data-testid={`companion-prompt-${i}`} className="card p-4 text-left text-sm hover:border-[#C86D51] chip disabled:opacity-50">{p}</button>
            ))}
          </div>
        )}
        {msgs.map((m, i) => <Bubble key={m.id || `${m.role}-${m.created_at}`} m={m} i={i}
          onOffers={(offers) => setMsgs((all) => all.map((x, k) => (k === i ? { ...x, offers } : x)))}
          onForget={v2 && m.id && !busy ? () => forget(i) : null}
          onPatch={v2 && m.id && m.content && !m.failed && !m.local && !busy ? (p) => setMsgs((all) => all.map((x, k) => (k === i ? { ...x, ...p } : x))) : null}
          onRetry={i === msgs.length - 1 && !busy ? () => { const u = [...msgs].reverse().find((x) => x.role === "user"); send(u?.content, true, u?.sent); } : null} />)}
        <div ref={endRef} />
      </div>

      <div className="chat-dock sticky bottom-[calc(4rem+env(safe-area-inset-bottom))] lg:bottom-0 mt-6 pt-3 pb-3 -mx-1 px-1 bg-[#FCFCFA]">
        {fromToday && <div className="mb-2 flex items-center gap-2 rounded-2xl bg-[#F0EBF2] px-3 py-2 text-xs text-[#392B43]" data-testid="chat-from-today">
          <span className="flex-1 min-w-0">From Today: <b>{fromToday.text}</b></span>
          <button onClick={() => setFromToday(null)} data-testid="chat-from-today-dismiss" className="h-11 px-2 underline">Don't include</button></div>}
        {outOfFree ? (
          <PlusGate testid="companion-plus-gate" title="You've used this month's free messages" body="Plus gives you unlimited Companion conversations that remember your recent days." />
        ) : (
          <Composer text={text} setText={setText} send={send} busy={busy} />
        )}
        {usage?.limit != null && !outOfFree && <p className="text-[11px] text-stone-400 text-center mt-2" data-testid="companion-usage">{usage.limit - usage.used} of {usage.limit} free messages left this month</p>}
      </div>
    </div>
  );
}
